import { memo, useState, useEffect, useCallback } from 'react';
import { Handle, Position, useReactFlow, useStore } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Button, message, Slider, Card, Select, Switch, Tooltip } from 'antd';
import { LoadingOutlined, CodepenOutlined, GatewayOutlined, DownloadOutlined, FullscreenOutlined, LogoutOutlined } from '@ant-design/icons';
import '@google/model-viewer';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useGeneration } from '../../../context/GenerationContext';
import api from '../../../api/client';
import NodeLabel from './NodeLabel';
import { useNodeTitle } from './useNodeTitle';
import { NODE_BG, TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';

const NEON_PURPLE = '#b026ff';

const Hunyuan3DNode = ({ id, data, selected }) => {
    const { projectId: canvasId } = useParams();
    const { setNodes } = useReactFlow();
    const [generating, setGenerating] = useState(false);
    const [hovered, setHovered] = useState(false);
    const onTitleChange = useNodeTitle(id, data);
    const { trackGeneration } = useGeneration();

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    // Optimized selector to get connected images
    const connectedInputs = useStore(
        useCallback((s) => {
            const edges = s.edges.filter((e) => e.target === id);
            return edges.reduce((acc, edge) => {
                const sourceNode = s.nodeInternals.get(edge.source);
                if (sourceNode?.data?.src) {
                    if (edge.targetHandle === 'input-front') acc.front = sourceNode.data.src;
                    if (edge.targetHandle === 'input-back') acc.back = sourceNode.data.src;
                    if (edge.targetHandle === 'input-left') acc.left = sourceNode.data.src;
                    if (edge.targetHandle === 'input-right') acc.right = sourceNode.data.src;
                }
                return acc;
            }, {});
        }, [id])
    );

    // Front is now the primary input
    const currentInput = connectedInputs.front || data.inputImage || null;

    // Local state for parameters (default matching API)
    const [params, setParams] = useState({
        face_count: data.face_count || 500000,
        generate_type: data.generate_type || 'Normal',
        polygon_type: data.polygon_type || 'triangle',
        enable_pbr: data.enable_pbr !== undefined ? data.enable_pbr : false,
    });

    const [modelUrl, setModelUrl] = useState(data.modelUrl || null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const updateData = useCallback((key, value) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return { ...node, data: { ...node.data, [key]: value } };
            }
            return node;
        }));
        if (data.onSave) data.onSave(id, { [key]: value });
    }, [id, setNodes, data]);

    // Sync params to data on change
    const updateParam = (key, value) => {
        setParams(prev => {
            const newParams = { ...prev, [key]: value };
            updateData(key, value);
            return newParams;
        });
    };

    const handleGenerate = async () => {
        if (!currentInput) {
            message.warning('Please connect a Front image.');
            return;
        }

        setGenerating(true);
        try {
            const payload = {
                // Map Front to input_image_url as it's the required main image
                input_image_url: currentInput,
                back_image_url: connectedInputs.back,
                left_image_url: connectedInputs.left,
                right_image_url: connectedInputs.right,
                face_count: params.face_count,
                generate_type: params.generate_type,
                enable_pbr: params.generate_type === 'Geometry' ? false : params.enable_pbr,
                canvas_id: canvasId,
                node_id: id
            };

            if (params.generate_type === 'LowPoly') {
                payload.polygon_type = params.polygon_type;
            }

            const response = await api.post('/fal/hunyuan3d/image-to-3d', payload);
            const { request_id } = response.data;
            const modelId = 'fal-ai/hunyuan3d-v3/image-to-3d';

            // setGenerating(false); // REMOVED

            trackGeneration(request_id, modelId, {
                prompt: 'Hunyuan 3D',
                type: '3D',
                canvas_id: canvasId,
                node_id: id,
                promptProp: `3D Generation (${params.generate_type})`
            }, {
                onComplete: (result) => {
                    if (result && result.model_glb) {
                        // The backend now handles uploading to storage and returns the persistent URL
                        let finalUrl = result.model_glb.url;

                        setModelUrl(finalUrl);
                        updateData('modelUrl', finalUrl);
                        // Also update the output 'src' for downstream nodes
                        updateData('src', finalUrl);

                        message.success('3D Model Generated!');
                    } else {
                        message.error('Generation failed - no model returned.');
                    }
                    setGenerating(false); // Stop glow
                },
                onFail: (err) => {
                    console.error("3D Generation failed via queue:", err);
                    setGenerating(false);
                }
            });

        } catch (error) {
            console.error('Hunyuan generation error:', error);
            message.error('Generation failed.');
            setGenerating(false); // Stop glow if submission fails
        }
    };

    const downloadModel = () => {
        if (modelUrl) {
            const link = document.createElement('a');
            link.href = modelUrl;
            link.download = `hunyuan_model_${id}.glb`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const { currentUser } = useAuth();
    const [token, setToken] = useState(null);

    useEffect(() => {
        if (currentUser) {
            currentUser.getIdToken().then(setToken);
        }
    }, [currentUser]);

    const renderViewer = (isFull = false) => (
        <div style={{ width: '100%', height: '100%', position: 'relative', background: isFull ? '#000' : 'transparent' }}>
            {modelUrl && token ? (
                <model-viewer
                    src={`${api.defaults.baseURL}/storage/proxy?url=${encodeURIComponent(modelUrl)}&token=${token}`}
                    camera-controls
                    auto-rotate
                    ar
                    shadow-intensity="1"
                    orbit-sensitivity="2.5"
                    touch-action="pan-y"
                    style={{ width: '100%', height: '100%' }}
                >
                </model-viewer>
            ) : (
                <div style={{
                    height: '100%', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    color: '#444', opacity: 0.6
                }}>
                    <CodepenOutlined style={{ fontSize: 48, marginBottom: 12 }} />
                    <div>No 3D Model</div>
                </div>
            )}

            {/* Overlay Controls */}
            {isFull && (
                <Button
                    type="text"
                    icon={<LogoutOutlined />}
                    style={{ position: 'absolute', top: 20, right: 20, color: '#fff', zIndex: 100 }}
                    onClick={() => setIsFullscreen(false)}
                >
                    Exit Fullscreen
                </Button>
            )}
        </div>
    );

    return (
        <div
            style={{ position: 'relative', height: '100%', width: '100%' }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer isVisible={selected} minWidth={600} minHeight={600} onResizeEnd={(e, p) => data.onResize && data.onResize(id, p)} />


            <NodeLabel
                title={data.nodeTitle || "Hunyuan 3D"}
                iconType="ant-design"
                icon={<CodepenOutlined />}
                color={NEON_PURPLE}
                onTitleChange={onTitleChange}
            />

            {/* Input Handle - Front */}
            <Handle
                type="target"
                position={Position.Left}
                id="input-front"
                style={{ ...getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible), top: '20%' }}
            />

            {/* Input Handle - Back */}
            <Handle
                type="target"
                position={Position.Left}
                id="input-back"
                style={{ ...getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible), top: '40%' }}
            />

            {/* Input Handle - Left */}
            <Handle
                type="target"
                position={Position.Left}
                id="input-left"
                style={{ ...getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible), top: '60%' }}
            />

            {/* Input Handle - Right */}
            <Handle
                type="target"
                position={Position.Left}
                id="input-right"
                style={{ ...getHandleStyle(TYPE_COLORS.image, 'left', handlesVisible), top: '80%' }}
            />

            {/* Output Handle */}
            <Handle
                type="source"
                position={Position.Right}
                id="output-model"
                style={getHandleStyle(TYPE_COLORS.image, 'right', handlesVisible)}
            />

            <Card
                size="small"
                bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column', background: NODE_BG }}
                style={{ width: '100%', height: '100%', border: '1px solid #30363d', background: NODE_BG, overflow: 'hidden', borderRadius: 8 }}
            >
                {/* 3D View Area - Takes full height */}
                <div
                    className="nodrag"
                    style={{
                        flex: 1,
                        background: '#0a0a0a',
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    {renderViewer(false)}

                    {modelUrl && (
                        <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 8, zIndex: 10 }}>
                            <Tooltip title="Fullscreen">
                                <Button size="small" icon={<FullscreenOutlined />} onClick={() => setIsFullscreen(true)} ghost />
                            </Tooltip>
                            <Tooltip title="Download GLB">
                                <Button size="small" icon={<DownloadOutlined />} onClick={downloadModel} ghost />
                            </Tooltip>
                        </div>
                    )}

                    {/* Floating Controls Overlay */}
                    <div className="nodrag" style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        padding: 12,
                        background: 'rgba(19, 22, 28, 0.9)',
                        backdropFilter: 'blur(4px)',
                        borderTop: '1px solid #30363d',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12
                    }}>

                        {/* Settings Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            {/* Type */}
                            <div>
                                <div style={{ fontSize: 10, color: '#777', marginBottom: 2 }}>GEN TYPE</div>
                                <Select
                                    size="small"
                                    style={{ width: '100%' }}
                                    value={params.generate_type}
                                    onChange={(v) => updateParam('generate_type', v)}
                                    options={[
                                        { label: 'Normal', value: 'Normal' },
                                        { label: 'Low Poly', value: 'LowPoly' },
                                        { label: 'Geometry', value: 'Geometry' }
                                    ]}
                                />
                            </div>

                            {/* PBR Toggle (if not Geometry) */}
                            <div>
                                <div style={{ fontSize: 10, color: '#777', marginBottom: 2 }}>PBR MATERIAL</div>
                                <Switch
                                    size="small"
                                    checked={params.enable_pbr}
                                    onChange={(v) => updateParam('enable_pbr', v)}
                                    disabled={params.generate_type === 'Geometry'}
                                />
                            </div>
                        </div>

                        {/* Low Poly Only Settings */}
                        {params.generate_type === 'LowPoly' && (
                            <div>
                                <div style={{ fontSize: 10, color: '#777', marginBottom: 2 }}>POLYGON TYPE</div>
                                <Select
                                    size="small"
                                    style={{ width: '100%' }}
                                    value={params.polygon_type}
                                    onChange={(v) => updateParam('polygon_type', v)}
                                    options={[
                                        { label: 'Triangle', value: 'triangle' },
                                        { label: 'Quad', value: 'quadrilateral' }
                                    ]}
                                />
                            </div>
                        )}

                        {/* Face Count Slider */}
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#777', marginBottom: 2 }}>
                                <span>FACE COUNT</span>
                                <span>{params.face_count.toLocaleString()}</span>
                            </div>
                            <Slider
                                min={40000}
                                max={1500000}
                                step={10000}
                                value={params.face_count}
                                onChange={(v) => updateParam('face_count', v)}
                                trackStyle={{ background: NEON_PURPLE }}
                                handleStyle={{ borderColor: NEON_PURPLE }}
                            />
                        </div>

                        <Button
                            type="primary"
                            block
                            icon={generating ? <LoadingOutlined /> : <GatewayOutlined />}
                            onClick={handleGenerate}
                            disabled={generating || !currentInput}
                            style={{
                                background: generating ? '#222' : NEON_PURPLE,
                                borderColor: generating ? '#444' : NEON_PURPLE,
                                height: 32,
                                marginTop: 4
                            }}
                        >
                            {generating ? 'GENERATING 3D...' : 'GENERATE 3D'}
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Fullscreen Modal View */}
            {isFullscreen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                    zIndex: 2000, background: '#000'
                }}>
                    {renderViewer(true)}
                </div>
            )}
        </div>
    );
};

export default memo(Hunyuan3DNode);
